/**
 * Notificações do NAVEGADOR (Notification API) para eventos notificáveis
 * (ticket e chat — a função é a mesma, muda só o texto e para onde o clique
 * navega).
 *
 * Regra dura: `Notification.requestPermission()` só é chamado a partir de uma
 * AÇÃO EXPLÍCITA do usuário (o switch "Notificações do navegador" na Minha
 * conta) — nunca no load. Pedir permissão sozinho no boot é a receita para o
 * usuário negar para sempre (e o Chrome pune com bloqueio automático).
 *
 * Sem permissão concedida, `showDesktopNotification` é no-op: quem "aparece" é
 * o sino + o toast in-app.
 */

export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

export function browserNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function browserNotificationPermission(): BrowserNotificationPermission {
  if (!browserNotificationsSupported()) return 'unsupported';
  return Notification.permission;
}

/** Chame SOMENTE dentro de um handler de clique/gesto do usuário. */
export async function requestBrowserNotificationPermission(): Promise<BrowserNotificationPermission> {
  if (!browserNotificationsSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export interface DesktopNotificationInput {
  title: string;
  body: string;
  /** dedupe do SO: reusa a mesma "vaga" por ticket/chat em vez de empilhar */
  tag: string;
  onClick: () => void;
}

/**
 * Mostra a notificação do SO. O clique foca a aba e chama `onClick` (que
 * navega para o ticket/conversa). Devolve false quando nada foi mostrado.
 */
export function showDesktopNotification(input: DesktopNotificationInput): boolean {
  if (browserNotificationPermission() !== 'granted') return false;
  try {
    // sem `icon`: o app não publica favicon estático — o SO usa o ícone do
    // navegador em vez de tentar (e falhar) baixar um /favicon.ico inexistente.
    const notification = new Notification(input.title, { body: input.body, tag: input.tag });
    notification.onclick = () => {
      window.focus();
      notification.close();
      input.onClick();
    };
    // Fecha sozinha: em alguns SOs a notificação fica presa no centro de
    // notificações até o usuário mexer.
    setTimeout(() => notification.close(), 12_000);
    return true;
  } catch {
    return false; // construtor pode lançar (ex.: Notification só via SW no Android)
  }
}
